import {
  createContext,
  useContext,
  type Dispatch,
  type SetStateAction,
} from 'react';

/** The activity a Clone starts from, and the stage it lives in. */
export type ActivityCloneSource = {
  activity: {
    id: number | string;
    kind?: string;
    label?: string;
    [key: string]: unknown;
  };
  stage: { id: number | string; name?: string; [key: string]: unknown };
};

/** Shape of `activityAction` when opening/editing a stage activity from the interview plan. */
export type InterviewStageActivityAction = {
  type?: string;
  stageId?: number | string;
  stage?: { id?: number | string; [key: string]: unknown };
  activity?: { id?: number | string; [key: string]: unknown };
  /**
   * Set when the drawer opens from Clone. `type` stays 'new' and `stage` is the
   * stage the copy is created in; forms load the prefill from here instead.
   */
  clone?: ActivityCloneSource;
};

export interface InterviewStageManagerValue {
  interviewPlan: unknown;
  activityDisclosure: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
  setInterviewPlan: (_plan: unknown) => void;
  selectedActivity: string;
  setSelectedActivity: (_value: string) => void;
  activityAction: InterviewStageActivityAction;
  setActivityAction: Dispatch<SetStateAction<InterviewStageActivityAction>>;
  isSettings: boolean;
  /**
   * Standalone Settings → Interview Plans only: edits a template, which never
   * holds candidates. NOT `isSettings` (that's the per-job settings sub-tab).
   */
  isInterviewPlanTemplate: boolean;
  disableActivity: boolean;
  jobId: unknown;
  refresh: () => void;
  canEditJob: boolean;
  scorecardParams: Record<string, unknown>;
  /**
   * Stage ids (stringified) the user has just created — those stage boxes
   * paint a short-lived highlight so the new row is easy to spot after the
   * scroll. A plain list, not a predicate: calling a callback from context
   * with `stage.id` makes the React Compiler skip optimising the stage row.
   */
  highlightedStageIds: string[];
  /**
   * Activity kinds the user may clone right now (same gates as Select activity).
   * `null` while the Clone activity feature is off — rows keep the trash icon.
   */
  cloneableKinds: ReadonlySet<string> | null;
}

const noop = () => {};

const defaultContextValue: InterviewStageManagerValue = {
  interviewPlan: null,
  activityDisclosure: { isOpen: false, onOpen: noop, onClose: noop },
  setInterviewPlan: noop,
  selectedActivity: 'select_activity',
  setSelectedActivity: noop,
  activityAction: {},
  setActivityAction: noop as Dispatch<
    SetStateAction<InterviewStageActivityAction>
  >,
  isSettings: false,
  isInterviewPlanTemplate: false,
  disableActivity: false,
  jobId: null,
  refresh: noop,
  canEditJob: false,
  scorecardParams: {},
  highlightedStageIds: [],
  cloneableKinds: null,
};

const InterviewStageManager =
  createContext<InterviewStageManagerValue>(defaultContextValue);

InterviewStageManager.displayName = 'InterviewStageManager';

export const useInterviewStageManager = () => useContext(InterviewStageManager);

export const InterviewStageProvider = InterviewStageManager.Provider;
